import { COLUMN_WIDTH } from "./layout";
import type { FlowDocument, FlowNodePosition } from "./types";

/**
 * The lowest-numbered "<type>_<n>" not already used in the document —
 * matches the schema's own id pattern (^[A-Za-z][A-Za-z0-9_-]{0,63}$) by
 * construction, so a freshly generated id never needs its own validation.
 */
export function nextNodeId(doc: FlowDocument, type: string): string {
  const used = new Set(doc.nodes.map((node) => node.id));
  let n = 1;
  let id = `${type}_${n}`;
  while (used.has(id)) {
    n += 1;
    id = `${type}_${n}`;
  }
  return id;
}

/**
 * Adds one new node and bakes in every EXISTING node's currently-displayed
 * position at the same time — the identical technique commitNodeDrag
 * (nodePosition.ts) uses, for the identical reason: layoutFlow only
 * respects a document's saved positions when EVERY node already has one,
 * so adding a single new positioned node without freezing the rest would
 * silently discard any layout the user already dragged into place on the
 * very next render.
 *
 * The new node's own position is a simple heuristic — to the right of the
 * current layout's rightmost node, at its topmost node's own height — not
 * a real "avoid every overlap" placement algorithm; the user can drag it
 * from there (Slice Y).
 *
 * The new node starts with no edges at all: connecting it into the graph
 * is a later slice (FlowCanvas.tsx's own `nodesConnectable={false}`
 * comment already names this as deliberately deferred, needing port-aware
 * semantics this slice does not attempt).
 */
export function addNode(
  doc: FlowDocument,
  currentPositions: Record<string, FlowNodePosition>,
  id: string,
  type: string,
  config: unknown,
  /** Where to place it — a palette drop point; omitted, it goes right of everything. */
  position?: FlowNodePosition,
): FlowDocument {
  function resolvedPosition(node: { id: string; position?: FlowNodePosition }): FlowNodePosition {
    return currentPositions[node.id] ?? node.position ?? { x: 0, y: 0 };
  }
  const resolved = doc.nodes.map(resolvedPosition);
  const maxX = resolved.reduce((max, p) => Math.max(max, p.x), 0);
  const minY = resolved.length > 0 ? Math.min(...resolved.map((p) => p.y)) : 0;
  const newPosition: FlowNodePosition = position ?? { x: maxX + COLUMN_WIDTH, y: minY };

  return {
    ...doc,
    nodes: [
      ...doc.nodes.map((node) => ({
        ...node,
        position: resolvedPosition(node),
      })),
      { id, type, config, position: newPosition },
    ],
  };
}

/**
 * Removes one node and every edge touching it (source or target).
 *
 * A dangling edge reference — pointing at a node that no longer exists —
 * is not something JSON Schema alone catches (validateFlow.ts's own
 * documented limit: "proves nothing about ports"), so this cleans edges
 * up structurally rather than leaving one behind.
 *
 * Refuses to remove the document's own start node: no valid document can
 * ever exist without one matching `start_node_id` (the schema's "exactly
 * one Start" rule), so this is a client-side guard even though no
 * semantic validation layer exists here yet — the same kind of guard
 * Inspector.tsx already applies to changing a node's type. Removing a
 * system-managed amd_gate node is NOT specially guarded here; that
 * protection is plan step 3's own separate, later designer slice
 * (docs/plans/06) — today, schema diagnostics are the only feedback a
 * user gets if they remove one.
 */
export function removeNode(doc: FlowDocument, nodeId: string): FlowDocument {
  if (nodeId === doc.start_node_id) {
    return doc;
  }
  return {
    ...doc,
    nodes: doc.nodes.filter((node) => node.id !== nodeId),
    edges: doc.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
  };
}
